import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { OrderedMap } from "immutable";
import CreateFilterDropdown from "components/common/EntityFilters/CreateFilterDropdown";
import ActiveFilters from "components/common/EntityFilters/ActiveFilters";
import useFiltersWithTitle from "components/common/EntityFilters/hooks/useFiltersWithTitle";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { defaultCompare } from "logic/DefaultCompare";
import { ROW_MIN_HEIGHT } from "./Constants";
const SUPPORTED_ATTRIBUTE_TYPES = ["STRING", "BOOLEAN", "DATE", "OBJECT_ID"];
const FilterCreation = styled.div`
  display: inline-flex;
  height: ${ROW_MIN_HEIGHT}px;
  align-items: center;
  margin-left: 5px;

  && {
    margin-right: 10px;
  }
`;
const EntityFilters = ({
  attributes = [],
  filterValueRenderers = void 0,
  urlQueryFilters,
  setUrlQueryFilters,
  appSection,
  activeSliceCol = void 0,
  activeSlice = void 0
}) => {
  const { t } = useTranslation("common");
  const sendTelemetry = useSendTelemetry(appSection);
  const { data: activeFilters, onChange: onChangeFiltersWithTitle } = useFiltersWithTitle(
    urlQueryFilters,
    attributes,
    !!attributes
  );
  const filterableAttributes = attributes.filter(({ filterable, type }) => filterable && SUPPORTED_ATTRIBUTE_TYPES.includes(type)).sort(({ title: title1 }, { title: title2 }) => defaultCompare(title1, title2));
  const onChangeFilters = useCallback(
    (newFilters) => {
      const newUrlQueryFilters = newFilters.entrySeq().reduce(
        (col, [attributeId, filterCol]) => {
          var _a;
          return col.set(attributeId, [...(_a = col.get(attributeId)) != null ? _a : [], ...filterCol.map(({ value }) => value)]);
        },
        OrderedMap()
      );
      onChangeFiltersWithTitle(newFilters, newUrlQueryFilters);
      setUrlQueryFilters(newUrlQueryFilters);
    },
    [onChangeFiltersWithTitle, setUrlQueryFilters]
  );
  const onCreateFilter = useCallback(
    (attributeId, filter) => {
      var _a;
      sendTelemetry(TELEMETRY_EVENT_TYPE.ENTITY_DATA_TABLE.FILTER_CREATED, {
        app_action_value: "filter-created",
        event_details: { attribute_id: attributeId }
      });
      onChangeFilters(OrderedMap(activeFilters).set(attributeId, [...(_a = activeFilters == null ? void 0 : activeFilters.get(attributeId)) != null ? _a : [], filter]));
    },
    [activeFilters, onChangeFilters, sendTelemetry]
  );
  const onDeleteFilter = useCallback(
    (attributeId, filterId) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.ENTITY_DATA_TABLE.FILTER_DELETED, {
        app_action_value: "filter-deleted",
        event_details: { attribute_id: attributeId }
      });
      const filterGroup = activeFilters.get(attributeId);
      const updatedFilterGroup = filterGroup.filter(({ value }) => value !== filterId);
      if (updatedFilterGroup.length) {
        return onChangeFilters(activeFilters.set(attributeId, updatedFilterGroup));
      }
      return onChangeFilters(activeFilters.remove(attributeId));
    },
    [activeFilters, onChangeFilters, sendTelemetry]
  );
  const onChangeFilter = useCallback(
    (attributeId, prevValue, newFilter) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.ENTITY_DATA_TABLE.FILTER_CHANGED, {
        app_action_value: "filter-value-changed",
        event_details: { attribute_id: attributeId }
      });
      const filterGroup = activeFilters.get(attributeId);
      const targetFilterIndex = filterGroup.findIndex(({ value }) => value === prevValue);
      const updatedFilterGroup = [...filterGroup];
      updatedFilterGroup[targetFilterIndex] = newFilter;
      onChangeFilters(activeFilters.set(attributeId, updatedFilterGroup));
    },
    [activeFilters, onChangeFilters, sendTelemetry]
  );
  if (!filterableAttributes.length) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FilterCreation, null, t("filters.filters"), /* @__PURE__ */ React.createElement(
    CreateFilterDropdown,
    {
      filterableAttributes,
      onCreateFilter,
      activeFilters,
      filterValueRenderers
    }
  )), activeFilters && /* @__PURE__ */ React.createElement(
    ActiveFilters,
    {
      filters: activeFilters,
      attributes,
      onChangeFilter,
      onDeleteFilter,
      filterValueRenderers,
      activeSliceCol,
      activeSlice
    }
  ));
};
export default EntityFilters;
